import { z } from 'zod';
import { Group } from '../../paths/herawApiPathGroups';
import { ApiMethod } from '../../types/api.types';
import { oaUserSchema } from '../../types/open-api/user.open-api.types';
import {
    authLoginPayload,
    authPasswordContraintsPayload,
    authPasswordResetPayload
} from '../../types/payloads/auth.payload';
import { emailSchema } from '../../types/payloads/user.payload';
import {
    passwordConstraintsSchema,
    workspaceParamsSchema
} from '../../types/payloads/workspace.payload';
import {
    apiKeyAuthenticationPayload,
    apiKeyAuthenticationResponseSchema,
    apiKeyRefreshSessionPayload,
    apiKeyRefreshSessionResponseSchema
} from '../../types/open-api/auth.open-api.types';

export const API_AUTH_PUBLIC = {
    GET: {
        method: ApiMethod.GET,
        path: '/auth/session',
        handlerMethod: 'refreshAccessToken',
        passResAndNextToHandler: true,
        isPublic: true
    },
    POST: {
        method: ApiMethod.POST,
        isPublic: true,
        path: '/auth/session',
        handlerMethod: 'login',
        passResAndNextToHandler: true,
        zod: {
            body: authLoginPayload
        }
    },
    POST_PLUGIN: {
        method: ApiMethod.POST,
        isPublic: true,
        path: '/public/v1/auth/plugin',
        handlerMethod: 'pluginLogin',
        zod: {
            body: z.object({
                pluginAuthToken: z.string().uuid()
            })
        },
        responses: {
            200: {
                description: "The user's api key",
                schema: z.object({
                    apiKey: z.string().nullable()
                })
            }
        }
    }
} as const;

export const API_AUTH_PUBLIC_PASSWORD = {
    RESET: {
        method: ApiMethod.PUT,
        path: '/auth/password/reset',
        passResAndNextToHandler: true,
        handlerMethod: 'resetPassword',
        isPublic: true,
        zod: {
            body: authPasswordResetPayload
        }
    },
    TOKEN_POST: {
        method: ApiMethod.POST,
        path: '/auth/password/token',
        handlerMethod: 'passwordTokenCreate',
        isPublic: true,
        passResAndNextToHandler: true,
        zod: {
            body: emailSchema
        }
    },
    CONSTRAINTS_GET: {
        method: ApiMethod.GET,
        path: '/auth/password/constraints',
        handlerMethod: 'getPasswordConstraints',
        isPublic: true,
        zod: {
            query: authPasswordContraintsPayload
        },
        responses: {
            200: {
                description: 'Password constraints',
                schema: passwordConstraintsSchema
            }
        }
    }
} as const;

export const API_AUTH_API_KEY = {
    GET_OA: {
        method: ApiMethod.GET,
        path: '/public/v1/workspaces/:workspaceName/session',
        handlerMethod: 'refreshApiKeyAccessToken',
        passResAndNextToHandler: true,
        publicApi: true,
        description: 'Refresh API key access token',
        group: Group.Authentication,
        zod: {
            params: workspaceParamsSchema,
            body: apiKeyRefreshSessionPayload
        },
        responses: {
            200: {
                description: 'New access token expiry in seconds',
                schema: apiKeyRefreshSessionResponseSchema
            }
        }
    },
    POST_OA: {
        method: ApiMethod.POST,
        path: '/public/v1/workspaces/:workspaceName/login',
        handlerMethod: 'loginWithApiKey',
        skipAuthMiddleware: true,
        publicApi: true,
        description: 'Authentication with a API key',
        group: Group.Authentication,
        zod: {
            params: workspaceParamsSchema,
            body: apiKeyAuthenticationPayload
        },
        responses: {
            200: {
                description: 'Authentication tokens',
                schema: apiKeyAuthenticationResponseSchema
            }
        },
        postman: {
            event: [
                {
                    listen: 'test',
                    script: {
                        exec: [
                            "pm.test('API key authentication', () => {",
                            '    const body = pm.response.json();',
                            "    pm.environment.set('ACCESS_TOKEN', body.accessToken);",
                            "    pm.environment.set('REFRESH_TOKEN', body.refreshToken);",
                            '});'
                        ],
                        type: 'text/javascript',
                        packages: {}
                    }
                }
            ],
            request: {
                auth: {
                    type: 'noauth'
                }
            }
        }
    }
} as const;
